import { relations } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { user } from '../auth'
import { BookCollection } from '../content'

// =============================================================================
// BOOK COLLECTION VIEW - Event table (one row per view)
// Aggregated into BookCollection.viewsCount by `cron / book-collection-metrics`.
// userId nullable so anonymous/public views are still counted.
// =============================================================================

export const BookCollectionView = pgTable(
  'book_collection_view',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text().references(() => user.id, { onDelete: 'set null' }),
    bookCollectionId: uuid()
      .references(() => BookCollection.id, { onDelete: 'cascade' })
      .notNull(),
    calculatedForMetrics: boolean().default(false).notNull(),
    metricsCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),
    calculatedForMonthlyMetrics: boolean().default(false).notNull(),
    monthlyMetricsCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (t) => [
    index('idx_book_collection_view_user').on(t.userId),
    index('idx_book_collection_view_book_collection').on(t.bookCollectionId),
    index('idx_book_collection_view_calculated').on(t.calculatedForMetrics),
    index('idx_book_collection_view_monthly_calculated').on(t.calculatedForMonthlyMetrics),
    index('idx_book_collection_view_created_at').on(t.createdAt),
  ],
)

export const bookCollectionViewRelations = relations(BookCollectionView, ({ one }) => ({
  bookCollection: one(BookCollection, {
    fields: [BookCollectionView.bookCollectionId],
    references: [BookCollection.id],
  }),
  user: one(user, {
    fields: [BookCollectionView.userId],
    references: [user.id],
  }),
}))

export const InsertBookCollectionViewSchema = createInsertSchema(BookCollectionView).omit({
  id: true,
  calculatedForMetrics: true,
  metricsCalculatedAt: true,
  createdAt: true,
})

export const SelectBookCollectionViewSchema = createSelectSchema(BookCollectionView)

export type InsertBookCollectionView = typeof BookCollectionView.$inferInsert
export type SelectBookCollectionView = typeof BookCollectionView.$inferSelect
